import { DocumentDialogAbsentContentLanguageComponent } from '@fi-app/document/dialog-absent-content-language/dialog-absent-content-language.component';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { AppTextService } from '@fi-shared/app-text';
import { DetectDevicesService, FiSnackBarService } from '@fi-shared/fi-shared-services';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { ContentLanguagesService } from '@fi-app/utilities/fi-services/content-languages.service';
import { Component, Input, OnInit } from '@angular/core';
import { IFormViewer, IOpenFormViewerParams } from '@fi-shared/fi-form-shared/fi-form-shared';
import { FiFormHelperService } from '@fi-shared/fi-form-shared/fi-form-helper.service';
import { StateDeclaration } from '@uirouter/angular';
import { StateAccessCheckService } from '@fi-shared/fi-shared-services/state-access-check.service';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { ObjectTypes } from '@fi-shared/types';
import { PageTitleService } from '@fi-app/utilities/fi-services/page-title.service';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { FiFormService } from '@fi-app/fi-form/fi-form.service';
import { FiItemsDeleteHelperService } from '@fi-shared/fi-shared-services/fi-items-delete-helper.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { EditTabsModel } from '@fi-app/utilities/edit-tabs/edit-tabs.model';
import { IContentLanguage } from '@fi-app/shared/fi-content-language-shared/fi-content-language-shared';
import { FiContentLanguageSharedService } from '@fi-shared/fi-content-language-shared/fi-content-language-shared.service';
import { FiItemsEditHelperService } from '@fi-shared/fi-shared-services/fi-items-edit-helper.service';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiFormActionsService } from '@fi-app/fi-form/fi-form-actions.service';
import { FiItemsListHelperService } from '@fi-shared/fi-items-list/fi-items-list-helper.service';

@Component({
    selector: 'fi-form-viewer-container',
    template: `
        <div class="fi-container fi-form-viewer-container">
          @if (!isMobilePhoneSmall()) {
            <fi-page-header
              [editTabsSettings]="EditTabs"
              [isMobileMenuShown]="false"
              [isSaveButtonShown]="false"
              >
              <ng-container ngProjectAs="left-aligned-content">
                <fi-print-download
                  class="hide-on-mobile"
                  [isNewLayout]="true"
                  [printElementSelector]="'#form_view'"
                  [downloadFilename]="getFormName()"
                  (onGeneratingPdfHtml)="onGeneratingPdfHtml($event)"
                  >
                  @if (isCopyEnabled) {
                    <button
                      mat-icon-button
                      class="BrandedColor"
                      [fiStateSref]="FiStateNames.Form.Copy"
                      [fiStateSrefParams]="{ id: formViewer.Id, selectedLanguageId: selectedLanguageId }"
                      [attr.title]="'common.create_copy' | fiAppText"
                      >
                      <mat-icon class="fas fa-copy"></mat-icon>
                    </button>
                  }
                </fi-print-download>
              </ng-container>
              <ng-container ngProjectAs="right-aligned-content">
                @if (!isMobilePhone()) {
                  <fi-page-header-button-group
                    [isMatRaisedButtonStyle]="true"
                    [isDefaultButton]="false"
                    [isDeleteButtonShown]="hasEditAccess()"
                    [isEditButtonShown]="hasEditAccess()"
                    (onEditButtonClick)="editForm()"
                    (onDeleteButtonClick)="deleteForm()"
                    >
                  </fi-page-header-button-group>
                }
              </ng-container>
            </fi-page-header>
          }
        
          <div class="fi-content">
            <fi-form-viewer
              [formViewer]="formViewer"
              [isMobileView]="isMobilePhoneSmall()"
              [languageList]="languageList"
              [selectedLanguageId]="selectedLanguageId"
              (showAnsweredForm)="showAnsweredForm($event)"
              (onSelectedLanguageIdChange)="onSelectedLanguageIdChange($event)"
              (onSave)="onSave()"
            ></fi-form-viewer>
          </div>
        </div>
        `,
    styleUrls: ['./fi-form-viewer-container.component.scss'],
    standalone: false,
})
export class FiFormViewerContainerComponent implements OnInit {
    @Input() formViewer: IFormViewer;
    @Input() previousState: {
        from: StateDeclaration;
        params: any;
    };

    languageList: IContentLanguage[];
    selectedLanguageId: number;
    defaultLanguageId: number;
    isCopyEnabled: boolean;
    FiStateNames = FiStateNames;
    EditTabs: EditTabsModel;

    constructor(
        private contentLanguagesService: ContentLanguagesService,
        private contentLanguageSharedService: FiContentLanguageSharedService,
        private fiDialogsService: FiDialogsService,
        private stateService: FiStateService,
        private formHelperService: FiFormHelperService,
        private detectDevice: DetectDevicesService,
        private snackBar: FiSnackBarService,
        private customerDataService: FiCustomerDataService,
        private stateAccessCheckService: StateAccessCheckService,
        private fiLoading: LoadingService,
        private appTextService: AppTextService,
        private pageTitleService: PageTitleService,
        private fiFormService: FiFormService,
        private itemsDeleteHelperService: FiItemsDeleteHelperService,
        private itemsEditHelperService: FiItemsEditHelperService,
        private fiUserDataService: FiUserDataService,
        private fiItemsListHelperService: FiItemsListHelperService,
        private fiFormActionsService: FiFormActionsService,
    ) {}

    ngOnInit() {
        this.selectedLanguageId = this.contentLanguageSharedService.getContentLanguageIdForDisplayContent(
            this.formViewer.Names,
        );
        this.defaultLanguageId = this.fiUserDataService.getContentLanguage();
        this.languageList = this.contentLanguagesService.GetContentLanguages();
        const formLanguageIds = this.getFormLangIds();
        this.languageList = this.languageList.filter((value) => -1 !== formLanguageIds.indexOf(value.Id));

        this.formHelperService.setQuestionTranslationsGroup(this.formViewer);
        if (!this.isFormQuestionsTranslatedToDefaultLang() && formLanguageIds.length > 1)
            this.handleNoDefaultTranslation(formLanguageIds);

        this.isCopyEnabled = this.stateAccessCheckService.isStateAllowedForCurrentUser(FiStateNames.Form.Add);

        this.pageTitleService.setTitleByName(this.formViewer.Name, ObjectTypes.Form);
        this.EditTabs = new EditTabsModel(this.getFormName(), this.appTextService.get('back'));
    }

    getFormName(): string {
        return this.contentLanguageSharedService.getContentLanguageValueForDisplayContent(
            this.formViewer.Names,
            this.selectedLanguageId,
        );
    }

    getFormLangIds(): number[] {
        return this.formViewer.Names.filter((name) => name.Value).map((item) => item.ContentLanguageId);
    }

    isFormQuestionsTranslatedToDefaultLang() {
        return this.formHelperService.isAnyQuestionTranslated(this.defaultLanguageId);
    }

    handleNoDefaultTranslation(availableIds: number[]) {
        this.selectedLanguageId = availableIds[0];

        this.fiDialogsService
            .custom({
                headerText: this.appTextService.get('common.oops'),
                yesButtonText: this.appTextService.get('form.view_form'),
                contentComponentRef: DocumentDialogAbsentContentLanguageComponent,
                contentComponentData: {
                    langIds: availableIds,
                    messageText: this.appTextService.get('form.dialog_absent_content_language_text'),
                },
                config: {
                    panelClass: 'dialog-content-padding',
                },
            })
            .onYesButtonClick((result) => (this.selectedLanguageId = result))
            .onNoButtonClick(() => window.history.back());
    }

    onSelectedLanguageIdChange(event: number) {
        this.selectedLanguageId = event;
        this.EditTabs.PageTitle = this.getFormName();
    }

    onSave() {
        this.snackBar.showSnackbar({
            textMessage: this.appTextService.get('form.completed'),
            buttonText: this.appTextService.get('common.dismiss'),
        });
        window.history.back();
    }

    hasEditAccess() {
        return this.formHelperService.isEditAllowedByModuleRights() && this.formViewer.HasEditAccess;
    }

    isMobilePhone() {
        return this.detectDevice.isMobilePhone();
    }

    isMobilePhoneSmall() {
        return this.detectDevice.isMobileSmall();
    }

    showAnsweredForm($event: IOpenFormViewerParams) {
        this.stateService.go(FiStateNames.Form.View, { id: $event.FormId, answerSetId: $event.AnswerSetId });
    }

    onGeneratingPdfHtml(isGeneratingPdfHtml: boolean) {
        this.formHelperService.onGeneratingPdfHtml(isGeneratingPdfHtml);
    }

    deleteForm() {
        this.fiFormActionsService
            .doDeleteForms([this.formViewer], this.fiItemsListHelperService.getDummyListMethods())
            .then((r) => {
                if (r.result) {
                    window.history.back();
                }
            });
    }

    editForm() {
        this.itemsEditHelperService.showEditRestoreConfirm(
            this.formViewer.Name,
            this.formViewer.IsDeleted,
            () => this.stateService.go(FiStateNames.Form.Edit, { id: this.formViewer.Id }),
            () => this.fiFormService.restoreForms([this.formViewer.Id]),
        );
    }
}
